import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import LinearProgress from '@mui/material/LinearProgress';
import usePolarisActor from 'src/hooks/usePolarisActor';
import useChatList from '../../hooks/useChatList';
import useResults from '../../hooks/useResults';
import { TREATMENT_GUIDANCE_QUESTIONS } from '../TreatmentGuidance/machine/constants';
import useIsPolaris from '../../hooks/useIsPolaris';
import { PXOC_QUESTIONS } from '../PXOCInterview/machine/questions';

import type { LinearProgressProps } from '@mui/material/LinearProgress';
import type { IMiniInterviewMachineContext } from 'src/machines/shared';
import type { MiniInterviewType } from 'src/packages/polaris/types';

export const StyledLinearProgress = styled(LinearProgress)`
  .MuiLinearProgress-bar {
    border-radius: 5px;
  }

  width: 100%;
  /* margin-top: 20px; */

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    order: 1;
    /* width: 143px; */
    margin-top: 0;
  }
`;

type GetExpectedNumberOfQuestionsOptions = {
  interviewType: MiniInterviewType | null;
  isInterviewHopping?: boolean;
};
const getExpectedNumberOfQuestions = ({
  interviewType,
  isInterviewHopping,
}: GetExpectedNumberOfQuestionsOptions) => {
  switch (interviewType) {
    case 'dxoc':
      return 12;
    case 'symptoms':
      return isInterviewHopping ? 15 : 25;
    case 'treatment':
      return 3;
    case 'treatmentGuidance':
      return TREATMENT_GUIDANCE_QUESTIONS.length;
    case 'pxoc':
      return PXOC_QUESTIONS.length;
    default:
      return undefined;
  }
};

type CalculateProgressOptions = {
  context: IMiniInterviewMachineContext;
  activeQuestionIndex: number;
  currentProgress?: number;
  isInterviewHopping?: boolean;
};
export const calculateProgress = ({
  context,
  activeQuestionIndex,
  currentProgress,
  isInterviewHopping,
}: CalculateProgressOptions) => {
  const { interviewType, questions } = context as any;

  const expectedNumberOfQuestions =
    questions?.length || getExpectedNumberOfQuestions({ interviewType, isInterviewHopping });

  if (!expectedNumberOfQuestions) {
    return;
  }

  const questionNumber = activeQuestionIndex + 1;

  const percentDone = 100 * (questionNumber / (expectedNumberOfQuestions + 1));

  if (percentDone < 100) {
    return percentDone;
  }

  if (currentProgress) {
    /*  hack just in case we go over the expected number of questions;
        halve the amount of progress bar left */
    return currentProgress + (100 - currentProgress) / 2;
  }
};

const ProgressBar = (props: LinearProgressProps) => {
  const state = usePolarisActor()?.getSnapshot() || {};
  const isPolaris = useIsPolaris();
  const chatList = useChatList();
  const result = useResults();
  const [progress, setProgress] = useState<number | undefined>(result ? 100 : undefined);
  const isInterviewHopping = state?.context?.isInterviewHop;
  const activeQuestion = chatList.items[chatList.active];

  useEffect(() => {
    if (
      result &&
      (state.context.interviewType !== 'txoc' ||
        (state.context.interviewType === 'txoc' && (result as any).isComplete))
    ) {
      return setProgress(100);
    } // Give preference to progress data returned from API
    if (activeQuestion?.interview_progress != undefined) {
      return setProgress(activeQuestion.interview_progress);
    }

    if (
      state.context?.interviewType === 'emergency' &&
      progress &&
      !activeQuestion?.interview_progress
    ) {
      // Hack to increment progress for the 'profile' question, which is constructed by the FE
      return setProgress(progress + 1);
    }
    setProgress(
      calculateProgress({
        context: state.context,
        activeQuestionIndex: chatList.active,
        currentProgress: progress,
        isInterviewHopping,
      }),
    );
  }, [activeQuestion, result, state.context]);

  if (!isPolaris && progress === undefined) {
    return null;
  }

  return (
    <StyledLinearProgress
      sx={{
        height: '8px',
        borderRadius: '5px',
      }}
      value={progress || 0}
      variant="determinate"
      {...props}
    />
  );
};

export default ProgressBar;
